import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi, useAuth } from '../../../hooks';
import { MeetingNote } from '../../types';

export const useUpdateMeetingStatus = () => {
  const queryClient = useQueryClient();
  const { api } = useApi();
  const { userToken } = useAuth();

  const mutation = useMutation({
    mutationFn: async ({
      meetingId,
      payload,
    }: {
      meetingId: number;
      payload: Partial<MeetingNote>;
    }) => {
      return await api({
        route: `/private/coach/meeting/${meetingId}`,
        token: userToken,
        method: 'PATCH',
        payload,
      });
    },

    onMutate: async ({ meetingId, payload }) => {
      // Setting pm_status always removes the card from UpcomingMeeting —
      // apply it to the cache immediately so the UI updates before the request finishes.
      if (!payload.pm_status) return;

      await queryClient.cancelQueries({ queryKey: ['hc-user-meetings'] });

      queryClient.setQueriesData<MeetingNote[]>(
        { queryKey: ['hc-user-meetings'] },
        (old) => {
          if (!old) return old;
          return old.map((meeting) =>
            meeting.healthie_appointment_id === meetingId
              ? { ...meeting, ...payload }
              : meeting
          );
        }
      );
    },

    onError: () => {
      // Refetch so the card reappears with the true (unset) status
      queryClient.invalidateQueries({ queryKey: ['hc-user-meetings'] });
    },

    onSuccess: () => {
      // delay invalidation - BE updates appt in Healthie, then receives webhook event & updates appt in Ayble db
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['hc-user-meetings'] });
        queryClient.invalidateQueries({
          queryKey: ['care-member-appointments'],
        });
        queryClient.invalidateQueries({ queryKey: ['appointment-types'] });
      }, 1000);
    },
  });

  return { mutation };
};
